/**
 * @fileoverview My Page table filter bar.
 * @description The labelled search box, the stage, status and priority dropdowns, the
 * include-closed dropdown and the Clear filters button, laid out inline under the table card's
 * title. It holds no state of its own: the screen passes the filters and gets each change back as a
 * patch. The Clear filters button is mounted but takes no room while no filter is active, so a
 * wrapped bar never leaves an empty row behind it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import {
  MY_PAGE_PRIORITY_OPTIONS,
  MY_PAGE_STAGE_OPTIONS,
  MY_PAGE_STATUS_OPTIONS,
} from '@/features/sales/constants/my-page.constants'
import { hasActiveMyPageFilters, MY_PAGE_ALL } from '@/features/sales/lib/my-page-filters.utils'
import Select from '@/shared/components/atoms/Select'

import TableSearchInput from './TableSearchInput'

import type { MyPageFilterState } from '@/features/sales/lib/my-page-filters.utils'
import type { ReactNode } from 'react'

/**
 * @component
 * @param {{ label: string; children: ReactNode }} props - The control and the label above it.
 */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-slate">{label}</span>
      {children}
    </div>
  )
}

/**
 * @component
 * @param {{ active: boolean; onClear: () => void }} props - Whether any filter is set, and the
 * click handler.
 * @param {boolean} props.active - Whether any filter is set; when false the button takes no room.
 * @param {() => void} props.onClear - Called when the button is clicked.
 */
function ClearFiltersButton({ active, onClear }: { active: boolean; onClear: () => void }) {
  const { t } = useTranslation()

  return (
    <button
      type="button"
      onClick={onClear}
      disabled={!active}
      aria-hidden={!active}
      data-testid="my-work-clear-filters-button"
      className={`rounded-lg border border-input-border bg-white px-3.5 py-2 text-[13px] font-semibold text-slate ${
        active ? 'cursor-pointer hover:border-border-hover hover:text-ink' : 'hidden'
      }`}
    >
      {t('sales.myWork.filters.clear')}
    </button>
  )
}

interface MyPageFiltersBarProps {
  filters: MyPageFilterState
  onChange: (patch: Partial<MyPageFilterState>) => void
  onClear: () => void
}

/**
 * @component
 * @param {MyPageFiltersBarProps} props - The filters and their handlers.
 * @param {MyPageFilterState} props.filters - The current filters.
 * @param {(patch: Partial<MyPageFilterState>) => void} props.onChange - Called with the filter
 * that changed.
 * @param {() => void} props.onClear - Called when Clear filters is clicked.
 */
function MyPageFiltersBar({ filters, onChange, onClear }: MyPageFiltersBarProps) {
  const { t } = useTranslation()
  const anyFilter = hasActiveMyPageFilters(filters)

  return (
    <div className="flex flex-wrap items-end gap-3.5 px-3.5 pb-3.5 sm:px-4 sm:pb-4">
      <Field label={t('sales.myWork.filters.search')}>
        <TableSearchInput
          value={filters.query}
          onChange={(query) => onChange({ query })}
          placeholder={t('sales.myWork.searchPlaceholder')}
          data-testid="my-work-search-input"
        />
      </Field>
      <Field label={t('sales.filterLabels.stage')}>
        <Select
          value={filters.stage}
          aria-label={t('sales.filterLabels.stage')}
          data-testid="my-work-stage-select"
          onChange={(event) =>
            onChange({ stage: event.target.value as MyPageFilterState['stage'] })
          }
          options={[
            { value: MY_PAGE_ALL, label: t('sales.myWork.filters.allStages') },
            ...MY_PAGE_STAGE_OPTIONS.map((option) => ({ value: option.code, label: option.label })),
          ]}
        />
      </Field>
      <Field label={t('sales.filterLabels.targetStatus')}>
        <Select
          value={filters.status}
          aria-label={t('sales.filterLabels.targetStatus')}
          data-testid="my-work-status-select"
          onChange={(event) =>
            onChange({ status: event.target.value as MyPageFilterState['status'] })
          }
          options={[
            { value: MY_PAGE_ALL, label: t('sales.myWork.filters.allStatuses') },
            ...MY_PAGE_STATUS_OPTIONS.map((value) => ({
              value,
              label: t(`sales.myWork.displayStatuses.${value}`),
            })),
          ]}
        />
      </Field>
      <Field label={t('sales.filterLabels.priority')}>
        <Select
          value={filters.priority}
          aria-label={t('sales.filterLabels.priority')}
          data-testid="my-work-priority-select"
          onChange={(event) =>
            onChange({ priority: event.target.value as MyPageFilterState['priority'] })
          }
          options={[
            { value: MY_PAGE_ALL, label: t('sales.myWork.filters.allPriorities') },
            ...MY_PAGE_PRIORITY_OPTIONS.map((value) => ({
              value,
              label: t(`sales.myWork.priorities.${value}`),
            })),
          ]}
        />
      </Field>
      <Field label={t('sales.myWork.filters.closedLabel')}>
        <Select
          value={filters.includeClosed ? 'include' : 'hide'}
          aria-label={t('sales.myWork.filters.closedLabel')}
          data-testid="my-work-include-closed-select"
          onChange={(event) => onChange({ includeClosed: event.target.value === 'include' })}
          options={[
            { value: 'include', label: t('sales.myWork.filters.includeClosed') },
            { value: 'hide', label: t('sales.myWork.filters.hideClosed') },
          ]}
        />
      </Field>
      <ClearFiltersButton active={anyFilter} onClear={onClear} />
    </div>
  )
}

export default MyPageFiltersBar
